/**
 * The shared UI kit. Small, unopinionated primitives — everything visual that appears more
 * than once lives here so the app stays consistent.
 *
 * Every component styles itself from the semantic tokens in styles.css, never a raw colour, so
 * light and dark are the same code path rather than two sets of overrides.
 */
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { Icon, type IconName } from "./icons";

export { Icon, type IconName };

export function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

/* Spinner ----------------------------------------------------------------- */

export function Spinner({ className = "size-4" }: { className?: string }) {
  return (
    <svg className={cx("animate-spin", className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/* Button ------------------------------------------------------------------ */

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "subtle";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  /** Stretches to the container. Clearer at a call site than remembering `w-full`. */
  block?: boolean;
};

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-accent-fg glow-accent hover:bg-accent-hover hover:brightness-[1.03] active:brightness-95",
  secondary:
    "border border-line bg-surface text-fg shadow-card hover:border-line-strong hover:bg-surface-raised",
  ghost: "text-fg-subtle hover:bg-tint hover:text-fg",
  subtle: "bg-tint text-fg hover:bg-tint-strong",
  danger: "border border-danger-line bg-danger-soft text-danger-text hover:bg-danger/20",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading, block, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        "relative inline-flex shrink-0 select-none items-center justify-center gap-2 rounded-2xl font-semibold",
        "transition-[transform,background-color,border-color,box-shadow,filter] duration-200 ease-out-quart",
        "active:scale-[0.97] disabled:pointer-events-none disabled:opacity-45",
        size === "sm" && "h-10 px-3.5 text-[13px]",
        size === "md" && "h-12 px-5 text-sm",
        size === "lg" && "h-14 px-7 text-base",
        block && "w-full",
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...props}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
});

/** A square button holding a single icon. Always needs an accessible label. */
export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    label: string;
    icon: IconName;
    variant?: ButtonVariant;
    size?: "sm" | "md";
  }
>(function IconButton({ label, icon, variant = "ghost", size = "md", className, ...props }, ref) {
  return (
    <button
      ref={ref}
      aria-label={label}
      title={label}
      className={cx(
        "inline-grid shrink-0 place-items-center rounded-xl transition-all duration-200",
        "active:scale-95 disabled:pointer-events-none disabled:opacity-45",
        size === "sm" ? "size-9" : "size-11",
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...props}
    >
      <Icon name={icon} className={size === "sm" ? "size-4" : "size-5"} />
    </button>
  );
});

/* Field shell -------------------------------------------------------------- */

const FIELD_BASE =
  "w-full rounded-2xl border bg-surface-sunken text-fg transition-[border-color,box-shadow,background-color] duration-200 " +
  "placeholder:text-fg-faint focus:outline-none focus-visible:outline-none";

/** 16px on mobile is deliberate — anything smaller makes iOS Safari zoom on focus. */
const FIELD_TEXT = "text-base sm:text-sm";

function fieldTone(hasError: boolean) {
  return hasError
    ? "border-danger-line focus:border-danger focus:shadow-[0_0_0_3px_var(--t-danger-soft)]"
    : "border-line hover:border-line-strong focus:border-accent-line focus:shadow-[0_0_0_3px_var(--t-accent-soft)]";
}

function FieldShell({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-fg-muted">
          {label}
        </label>
      )}
      {children}
      {error ? (
        <p id={`${id}-error`} className="flex items-center gap-1.5 text-[13px] text-danger-text">
          <Icon name="alert" className="size-3.5" />
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-[13px] text-fg-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/* Input -------------------------------------------------------------------- */

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  hint?: string;
  error?: string;
  /** Decorative leading icon. Padding adjusts automatically. */
  icon?: IconName;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, icon, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? props.name ?? generatedId;

  return (
    <FieldShell id={inputId} label={label} hint={hint} error={error}>
      <div className="relative">
        {icon && (
          <Icon
            name={icon}
            className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-fg-faint"
          />
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          className={cx(
            FIELD_BASE,
            FIELD_TEXT,
            fieldTone(Boolean(error)),
            "h-12",
            icon ? "pl-10 pr-3.5" : "px-3.5",
            className,
          )}
          {...props}
        />
      </div>
    </FieldShell>
  );
});

/* Textarea ----------------------------------------------------------------- */

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  hint?: string;
  error?: string;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? props.name ?? generatedId;

  return (
    <FieldShell id={fieldId} label={label} hint={hint} error={error}>
      <textarea
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
        className={cx(FIELD_BASE, FIELD_TEXT, fieldTone(Boolean(error)), "resize-y p-3.5", className)}
        {...props}
      />
    </FieldShell>
  );
});

/* Select ------------------------------------------------------------------- */

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string;
  hint?: string;
  error?: string;
};

/**
 * A native `<select>` with our chrome. Native is deliberate: it gives us the platform's own
 * picker on mobile, which is both faster and more familiar than anything custom.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, className, id, children, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? props.name ?? generatedId;

  return (
    <FieldShell id={fieldId} label={label} hint={hint} error={error}>
      <div className="relative">
        <select
          ref={ref}
          id={fieldId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          className={cx(
            FIELD_BASE,
            FIELD_TEXT,
            fieldTone(Boolean(error)),
            "h-12 appearance-none py-0 pl-3.5 pr-10",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          {...props}
        >
          {children}
        </select>
        <Icon
          name="chevronDown"
          className="pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-fg-faint"
        />
      </div>
    </FieldShell>
  );
});

/* Switch ------------------------------------------------------------------- */

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        "relative h-8 w-14 shrink-0 rounded-full border transition-colors duration-250",
        "disabled:cursor-not-allowed disabled:opacity-45",
        checked ? "border-accent-line bg-accent" : "border-line bg-surface-sunken",
      )}
    >
      {/* `left` must be explicit: with `left: auto` the knob is placed at its static position,
          which a button's UA `text-align: center` puts in the middle of the track. Track is
          56×32 with a 1px border, so a 24px knob sits 3px in on every side and travels 24px. */}
      <span
        className={cx(
          "absolute left-[3px] top-1/2 size-6 -translate-y-1/2 rounded-full shadow-sm",
          "transition-transform duration-250 ease-spring",
          checked ? "translate-x-6 bg-accent-fg" : "translate-x-0 bg-fg-faint",
        )}
      />
    </button>
  );
}

/* Segmented control -------------------------------------------------------- */

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  size = "md",
  className,
}: {
  value: T;
  onChange: (next: T) => void;
  options: Array<{ value: T; label: string; icon?: IconName }>;
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      className={cx(
        "inline-flex rounded-2xl border border-line bg-surface-sunken p-1",
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cx(
              "inline-flex items-center justify-center gap-1.5 rounded-xl font-semibold transition-all duration-200",
              size === "sm" ? "h-8 px-3 text-[12px]" : "h-10 px-4 text-[13px]",
              active
                ? "bg-accent text-accent-fg shadow-sm"
                : "text-fg-subtle hover:text-fg",
            )}
          >
            {option.icon && <Icon name={option.icon} className="size-4" />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/* Tabs --------------------------------------------------------------------- */

/**
 * Horizontal tabs with an indicator that slides between them. The indicator is measured from
 * the active tab rather than assuming equal widths, and re-measured on resize — otherwise it
 * drifts off the label as soon as the font loads or the container changes size.
 */
export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
  className,
}: {
  value: T;
  onChange: (next: T) => void;
  tabs: Array<{ value: T; label: string; icon?: IconName; badge?: number }>;
  className?: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const measure = () => {
      const active = list.querySelector<HTMLElement>('[data-active="true"]');
      if (!active) return;
      const next = { left: active.offsetLeft, width: active.offsetWidth };
      // Bail out when nothing moved. Callers build the `tabs` array inline, so this effect
      // re-runs on every render — without returning the identical object, each run would
      // set state, trigger another render, and loop forever.
      setIndicator((prev) =>
        prev && prev.left === next.left && prev.width === next.width ? prev : next,
      );
    };

    measure();

    // Fonts loading, the container resizing, or an orientation change all move the target.
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    for (const child of Array.from(list.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [value, tabs]);

  // Keep the selected tab in view when the strip scrolls horizontally on a phone.
  useEffect(() => {
    const active = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    active?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  }, [value]);

  return (
    <div
      className={cx(
        "mobile-scroll-tabs relative -mx-4 overflow-x-auto border-b border-line px-4 sm:mx-0 sm:px-0",
        className,
      )}
    >
      <div ref={listRef} role="tablist" className="relative flex w-max gap-1">
        {tabs.map((tab) => {
          const active = tab.value === value;
          return (
            <button
              key={tab.value}
              role="tab"
              type="button"
              aria-selected={active}
              data-active={active}
              onClick={() => onChange(tab.value)}
              className={cx(
                "relative flex min-h-11 shrink-0 items-center gap-2 px-3.5 py-3 text-sm font-medium transition-colors duration-200",
                active ? "text-fg" : "text-fg-subtle hover:text-fg-muted",
              )}
            >
              {tab.icon && <Icon name={tab.icon} className="size-4" />}
              {tab.label}
              {tab.badge != null && tab.badge > 0 && (
                <span className="tabular rounded-full bg-tint-strong px-1.5 py-0.5 text-[11px] font-semibold text-fg-muted">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}

        {indicator && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 h-0.5 rounded-full bg-accent transition-[left,width] duration-300 ease-out-quart"
            style={{ left: indicator.left, width: indicator.width }}
          />
        )}
      </div>
    </div>
  );
}

/* Badge -------------------------------------------------------------------- */

type BadgeTone = "alive" | "out" | "neutral" | "warn" | "info" | "accent";

const BADGE_TONES: Record<BadgeTone, string> = {
  alive: "bg-accent-soft text-accent-text ring-accent-line",
  accent: "bg-accent-soft text-accent-text ring-accent-line",
  out: "bg-danger-soft text-danger-text ring-danger-line",
  warn: "bg-warn-soft text-warn-text ring-warn-line",
  info: "bg-info-soft text-info-text ring-info-line",
  neutral: "bg-tint text-fg-subtle ring-line",
};

export function Badge({
  tone = "neutral",
  children,
  className,
  icon,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
  icon?: IconName;
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1",
        "text-[11px] font-semibold uppercase tracking-wider ring-1",
        BADGE_TONES[tone],
        className,
      )}
    >
      {icon && <Icon name={icon} className="size-3" strokeWidth={2.2} />}
      {children}
    </span>
  );
}

/* Avatar ------------------------------------------------------------------- */

export function Avatar({
  name,
  color,
  size = 32,
  dimmed,
  ring,
}: {
  name: string;
  color: string;
  size?: number;
  dimmed?: boolean;
  ring?: boolean;
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <span
      aria-hidden="true"
      className={cx(
        "inline-grid shrink-0 place-items-center rounded-full font-bold text-white transition-opacity",
        dimmed && "opacity-40 grayscale",
        ring && "ring-2 ring-accent ring-offset-2 ring-offset-[var(--t-surface)]",
      )}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        background: `linear-gradient(140deg, ${color}, ${color}b0)`,
        textShadow: "0 1px 2px rgb(0 0 0 / 0.25)",
      }}
    >
      {initials}
    </span>
  );
}

/* Crest -------------------------------------------------------------------- */

/**
 * A club badge, falling back to a monogram tile. Crests come from provider-controlled hosts, so
 * a missing or broken image is normal rather than exceptional — `onError` swaps to the fallback
 * instead of leaving a torn-image icon in the middle of the team grid.
 */
export function Crest({
  url,
  name,
  size = 32,
  className,
}: {
  url: string | null;
  name: string;
  size?: number;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);

  if (!url || failed) {
    return (
      <span
        className={cx(
          "grid shrink-0 place-items-center rounded-lg bg-tint-strong font-bold text-fg-subtle",
          className,
        )}
        style={{ width: size, height: size, fontSize: size * 0.42 }}
        aria-hidden="true"
      >
        {name.slice(0, 1).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={cx("shrink-0 object-contain", className)}
      style={{ width: size, height: size }}
    />
  );
}

/* Countdown ---------------------------------------------------------------- */

function useCountdown(to: string) {
  const [remaining, setRemaining] = useState(() => new Date(to).getTime() - Date.now());

  useEffect(() => {
    setRemaining(new Date(to).getTime() - Date.now());
    const timer = setInterval(() => setRemaining(new Date(to).getTime() - Date.now()), 1000);
    return () => clearInterval(timer);
  }, [to]);

  return remaining;
}

/**
 * The deadline is the single most important number on the competition page, so it gets the
 * loudest treatment: tabular figures that never jitter, and amber inside the last hour.
 */
export function Countdown({
  to,
  className,
  variant = "text",
}: {
  to: string;
  className?: string;
  variant?: "text" | "blocks";
}) {
  const remaining = useCountdown(to);

  if (remaining <= 0) {
    return <span className={cx("font-semibold text-danger-text", className)}>Locked</span>;
  }

  const seconds = Math.floor(remaining / 1000);
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const urgent = remaining < 3_600_000;

  if (variant === "blocks") {
    const blocks = days > 0
      ? [
          { value: days, unit: "d" },
          { value: hours, unit: "h" },
          { value: minutes, unit: "m" },
        ]
      : [
          { value: hours, unit: "h" },
          { value: minutes, unit: "m" },
          { value: secs, unit: "s" },
        ];

    return (
      <span className={cx("flex items-center gap-1.5", className)} title={new Date(to).toLocaleString()}>
        {blocks.map((block) => (
          <span
            key={block.unit}
            className={cx(
              "flex min-w-[2.75rem] flex-col items-center rounded-xl border px-1.5 py-1",
              urgent ? "border-warn-line bg-warn-soft" : "border-line bg-tint",
            )}
          >
            <span
              className={cx(
                "tabular font-display text-xl font-bold leading-none",
                urgent ? "text-warn-text" : "text-fg",
              )}
            >
              {String(block.value).padStart(2, "0")}
            </span>
            <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-fg-faint">
              {block.unit}
            </span>
          </span>
        ))}
      </span>
    );
  }

  return (
    <span
      className={cx("tabular font-semibold", urgent ? "text-warn-text" : "text-fg", className)}
      title={new Date(to).toLocaleString()}
    >
      {days > 0 && `${days}d `}
      {(days > 0 || hours > 0) && `${hours}h `}
      {days === 0 && `${minutes}m `}
      {days === 0 && hours === 0 && `${secs}s`}
    </span>
  );
}

/* Layout ------------------------------------------------------------------- */

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: ReactNode;
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-7 flex flex-col items-stretch justify-between gap-5 sm:mb-9 sm:flex-row sm:items-end">
      <div className="min-w-0">
        {eyebrow && <div className="label mb-2">{eyebrow}</div>}
        <h1 className="display-title text-[2.5rem] text-fg sm:text-5xl">{title}</h1>
        {subtitle && (
          <div className="mt-2.5 max-w-2xl text-sm leading-relaxed text-fg-subtle">{subtitle}</div>
        )}
      </div>
      {actions && <div className="flex shrink-0 gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">{actions}</div>}
    </header>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="card flex flex-col items-center px-6 py-12 text-center sm:py-16">
      {icon && (
        <div className="mb-5 grid size-14 place-items-center rounded-2xl border border-accent-line bg-accent-soft text-accent-text">
          {icon}
        </div>
      )}
      <h3 className="display-title text-2xl text-fg">{title}</h3>
      {description && (
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-fg-subtle">{description}</p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx("skeleton rounded-xl", className)} />;
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 rounded-xl border border-danger-line bg-danger-soft px-3.5 py-2.5 text-sm text-danger-text"
    >
      <Icon name="alert" className="mt-0.5 size-4" />
      <span className="min-w-0">{children}</span>
    </div>
  );
}

export function SuccessNote({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-xl border border-accent-line bg-accent-soft px-3.5 py-2.5 text-sm text-accent-text"
    >
      <Icon name="check" className="mt-0.5 size-4" />
      <span className="min-w-0">{children}</span>
    </div>
  );
}

export function InfoNote({ children, icon = "info" }: { children: ReactNode; icon?: IconName }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-line bg-tint px-3.5 py-2.5 text-sm leading-relaxed text-fg-subtle">
      <Icon name={icon} className="mt-0.5 size-4 text-fg-faint" />
      <span className="min-w-0">{children}</span>
    </div>
  );
}

/** A single headline number. Used for the overview strips on the admin and competition pages. */
export function StatTile({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "neutral" | "alive" | "out" | "warn";
}) {
  return (
    <div className="card p-4">
      <p className="label mb-1.5">{label}</p>
      <p
        className={cx(
          "tabular font-display text-3xl font-bold leading-none",
          tone === "alive" && "text-accent-text",
          tone === "out" && "text-danger-text",
          tone === "warn" && "text-warn-text",
          tone === "neutral" && "text-fg",
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-1.5 text-[12px] text-fg-subtle">{hint}</p>}
    </div>
  );
}

export function ProgressBar({
  value,
  max,
  className,
  label,
}: {
  value: number;
  max: number;
  className?: string;
  label?: string;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
      className={cx("h-1.5 overflow-hidden rounded-full bg-tint-strong", className)}
    >
      <div
        className="h-full rounded-full bg-gradient-to-r from-accent-hover to-accent transition-[width] duration-700 ease-out-quart"
        style={{ width: `${Math.max(pct, value > 0 ? 4 : 0)}%` }}
      />
    </div>
  );
}

/* Modal -------------------------------------------------------------------- */

/**
 * Locks background scroll while a dialog is open.
 *
 * Position-fixing the body rather than just `overflow: hidden` is deliberate: iOS Safari
 * happily scrolls the page behind an overlay otherwise. The scroll offset is captured and
 * restored so the page doesn't jump to the top when the dialog closes. A counter keeps nested
 * or overlapping dialogs from unlocking each other early.
 */
let scrollLockCount = 0;
let storedScrollY = 0;

function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;

    if (scrollLockCount === 0) {
      storedScrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${storedScrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.overflow = "hidden";
    }
    scrollLockCount += 1;

    return () => {
      scrollLockCount -= 1;
      if (scrollLockCount === 0) {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.overflow = "";
        window.scrollTo(0, storedScrollY);
      }
    };
  }, [active]);
}

const FOCUSABLE =
  "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex='-1'])";

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  width = "max-w-lg",
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  width?: string;
  /** Pinned below the scrollable body — keeps the primary action reachable on a phone. */
  footer?: ReactNode;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Kept mounted for the length of the exit transition, so closing animates instead of blinking.
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Two frames: the first lets the closed state commit to the DOM, the second transitions
      // away from it. Without that the element mounts already-open and there's no animation.
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }

    setVisible(false);
    const timer = window.setTimeout(() => setMounted(false), 220);
    return () => window.clearTimeout(timer);
  }, [open]);

  useScrollLock(mounted);

  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement as HTMLElement;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!focusable.length) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);

    const focusFrame = requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog || dialog.contains(document.activeElement)) return;
      // Prefer a real field over the close button — landing on "close" reads as hostile.
      const preferred = dialog.querySelector<HTMLElement>(
        "input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]):not([data-modal-close])",
      );
      (preferred ?? dialog.querySelector<HTMLElement>("button:not([disabled])"))?.focus();
    });

    const restoreTo = triggerRef.current;
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKey);
      // Returning focus where it came from is what makes keyboard navigation feel unbroken.
      restoreTo?.focus?.();
    };
  }, [open]);

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div
        onClick={onClose}
        aria-hidden="true"
        className={cx(
          "absolute inset-0 bg-[var(--t-scrim)] backdrop-blur-sm transition-opacity duration-200",
          visible ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={cx(
          "relative flex max-h-[92dvh] w-full flex-col overflow-hidden border border-line bg-surface",
          "rounded-t-card sm:rounded-card",
          "shadow-pop",
          "transition-[opacity,transform] duration-250 ease-out-quart",
          visible
            ? "translate-y-0 opacity-100 sm:scale-100"
            : "translate-y-6 opacity-0 sm:translate-y-2 sm:scale-[0.97]",
          width,
        )}
      >
        <div className="flex items-start justify-between gap-4 px-5 pb-4 pt-5 sm:px-6 sm:pt-6">
          <div className="min-w-0">
            <span
              className="mx-auto mb-3 block h-1 w-10 rounded-full bg-line-strong sm:hidden"
              aria-hidden="true"
            />
            <h2 id={titleId} className="display-title text-2xl text-fg">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-1.5 text-sm leading-relaxed text-fg-subtle">
                {description}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-modal-close
            className="-mr-1.5 grid size-10 shrink-0 place-items-center rounded-xl text-fg-subtle transition-colors hover:bg-tint hover:text-fg"
          >
            <Icon name="close" />
          </button>
        </div>

        <div className="mobile-scroll-tabs min-h-0 flex-1 overflow-y-auto px-5 pb-5 sm:px-6 sm:pb-6">
          {children}
        </div>

        {footer && (
          <div className="safe-bottom border-t border-line bg-surface px-5 py-4 sm:px-6">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/* Toasts ------------------------------------------------------------------- */

interface Toast {
  id: number;
  message: string;
  tone: "success" | "error" | "info";
}

interface ToastValue {
  show: (message: string, tone?: Toast["tone"]) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

/**
 * Transient confirmation for actions whose result isn't otherwise visible — copying a link,
 * saving a setting. Anything the user must acknowledge belongs in a dialog instead; this
 * disappears on its own and is never the only place an error is reported.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const show = useCallback((message: string, tone: Toast["tone"] = "success") => {
    const id = nextId.current++;
    setToasts((current) => [...current.slice(-2), { id, message, tone }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 3600);
  }, []);

  const value = useMemo<ToastValue>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          aria-atomic="false"
          className="safe-bottom pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 px-4 pb-[5.5rem] sm:pb-6"
        >
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={cx(
                "animate-pop pointer-events-auto flex max-w-sm items-center gap-2.5 rounded-2xl border px-4 py-3 text-sm font-medium shadow-pop",
                toast.tone === "success" && "border-accent-line bg-surface text-accent-text",
                toast.tone === "error" && "border-danger-line bg-surface text-danger-text",
                toast.tone === "info" && "border-line bg-surface text-fg",
              )}
            >
              <Icon
                name={toast.tone === "success" ? "check" : toast.tone === "error" ? "alert" : "info"}
                className="size-4"
              />
              {toast.message}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  const context = useContext(ToastContext);
  // Deliberately forgiving: a toast is never load-bearing, so a component rendered outside the
  // provider (a test, a detached preview) should still work rather than throw.
  return context ?? { show: () => {} };
}

/* Copy-to-clipboard -------------------------------------------------------- */

/** Returns a `copy` function and a `copied` flag that resets itself after a moment. */
export function useCopy(resetAfterMs = 2000) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), resetAfterMs);
        return true;
      } catch {
        return false;
      }
    },
    [resetAfterMs],
  );

  return { copied, copy };
}

/* Formatting --------------------------------------------------------------- */

export function formatKickoff(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** "Sat 15 Aug" — the day a match falls on, without repeating a time shown elsewhere. */
export function formatMatchDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** "3 days ago" / "in 2 hours" — for audit trails and last-seen stamps. */
export function formatRelative(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(diff);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 31_536_000_000],
    ["month", 2_592_000_000],
    ["day", 86_400_000],
    ["hour", 3_600_000],
    ["minute", 60_000],
  ];

  for (const [unit, ms] of units) {
    if (abs >= ms) return formatter.format(Math.round(diff / ms), unit);
  }
  return "just now";
}
